import { APIPayeeStatus, APIPaymentStatus, APIUserStatus } from 'types/backend';

import { palette } from 'styles/theme';

export const SUPPORT_EMAIL = 'support@EasyCheck.org';

export const INFINITE_SCROLL_BOTTOM_OFFSET_PX = 300;
export const INFINITE_SCROLL_DEBOUNCE_TIME_MS = 3000;
export const TOOLTIP_APPEAR_DELAY = 1000;

export const ICON_SIZE_SM = 20;
export const ICON_SIZE_MD = 24;

export const CENT_DIGITS = 2;

export const REFRESH_TOKEN_IN_MINUTES = 10;
export const DEFAULT_PAGE_SIZE = 20;
export const USER_INPUT_DEBOUNCE_TIME_MS = 500;

export const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';
export const DISPLAY_DATE_TIME_FORMAT = 'MM/DD/YYYY hh:mm A';

export const DEFAULT_TIMEZONE = 'America/New_York';

// Common US time zones shown at the top of the Settings timezone dropdown.
// Values are IANA identifiers so they work with dayjs on the client and
// TimeZoneInfo.FindSystemTimeZoneById on the API.
export const US_TIMEZONES: { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Arizona (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska Time (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (Honolulu)' },
];

export const PaymentColors = {
  [APIPaymentStatus.Draft]: '#D3D3D3',
  [APIPaymentStatus.Requested]: '#addce6',
  [APIPaymentStatus.Created]: '#fbf198',
  [APIPaymentStatus.Void]: '#FFB6C1',
  [APIPaymentStatus.TryToPrint]: '#B0E0E6',
  [APIPaymentStatus.OpenToPrint]: '#FFA07A',
  [APIPaymentStatus.CouldNotPrint]: '#FFA07A',
  [APIPaymentStatus.Printed]: '#90EE90',
  [APIPaymentStatus.Emailed]: '#98FB98',
  [APIPaymentStatus.Delivered]: '#9ed0eb',
  [APIPaymentStatus.NotDelivered]: '#F08080',
  [APIPaymentStatus.Cleared]: '#90EE90',
  [APIPaymentStatus.Scheduled]: '#FFA500',
};

export const PaymentStatus = {
  [APIPaymentStatus.Draft]: 'Draft',
  [APIPaymentStatus.Requested]: 'Requested',
  [APIPaymentStatus.Created]: 'Created',
  [APIPaymentStatus.Void]: 'Void',
  [APIPaymentStatus.TryToPrint]: 'Try To Print',
  [APIPaymentStatus.OpenToPrint]: 'Open To Print',
  [APIPaymentStatus.CouldNotPrint]: 'Could Not Print',
  [APIPaymentStatus.Printed]: 'Printed',
  [APIPaymentStatus.Emailed]: 'Emailed',
  [APIPaymentStatus.Delivered]: 'Delivered',
  [APIPaymentStatus.NotDelivered]: 'Not delivered',
  [APIPaymentStatus.Cleared]: 'Cleared',
  [APIPaymentStatus.Scheduled]: 'Scheduled',
};

export const UserStatusColors = {
  [APIUserStatus.Active]: palette.success100,
  [APIUserStatus.NotRegistered]: palette.warning100,
  [APIUserStatus.Inactive]: palette.muted100,
  [APIUserStatus.Deleted]: palette.error100,
};

// Inactive is never assigned and Deleted users are no longer org members, so neither can match.
export const MEMBER_FILTER_USER_STATUSES = [
  APIUserStatus.Active,
  APIUserStatus.NotRegistered,
];

export const UserStatusRowColors: Partial<Record<APIUserStatus, string>> = {
  [APIUserStatus.NotRegistered]: palette.warning50,
};

export const PayeeColors = {
  [APIPayeeStatus.NotActive]: '#F08080',
  [APIPayeeStatus.Deleted]: '#FFB6C1',
  [APIPayeeStatus.Archive]: '#D3D3D3',
  [APIPayeeStatus.Active]: '#90EE90',
  [APIPayeeStatus.Rejected]: '#FFA07A',
  [APIPayeeStatus.PendingForReview]: '#fbf198',
};

export const PayeeStatus = {
  [APIPayeeStatus.NotActive]: 'Not Active',
  [APIPayeeStatus.Deleted]: 'Deleted',
  [APIPayeeStatus.Archive]: 'Archive',
  [APIPayeeStatus.Active]: 'Active',
  [APIPayeeStatus.Rejected]: 'Rejected',
  [APIPayeeStatus.PendingForReview]: 'Pending For Review',
};

export enum OrganizationType {
  Live = 'live',
  Sandbox = 'sandbox',
  Disabled = 'disabled',
}

export enum PersisTables {
  Members = 'members',
  Banks = 'banks',
  Payments = 'payments',
  Payees = 'payees',
  Audits = 'audits',
  ApiClientTokens = 'apiclienttokens',
}

export enum PersisTableSort {
  Members = 'members_sort',
  Banks = 'banks_sort',
  Payments = 'payments_sort',
  Payees = 'payees_sort',
  Audits = 'audits_sort',
  ApiClientTokens = 'apiclienttokens_sort',
}

export enum PersisTableFilters {
  Members = 'members_filters',
  Banks = 'banks_filters',
  Payments = 'payments_filters',
  Payees = 'payees_filters',
  Audits = 'audits_filters',
  ApiClientTokens = 'apiclienttokens_filters',
}

export const TAWK_PROPERTY_ID = process.env['TAWK_PROPERTY_ID'];
export const TAWK_WIDGET_ID = process.env['TAWK_WIDGET_ID'];

// Hides billing from customers without touching plan limits; anything but 'false' keeps it shown.
export const BILLING_ENABLED = !/^\s*false\s*$/i.test(
  process.env['BILLING_ENABLED'] ?? '',
);
export const DEFAULT_TOP_UP_QUANTITY = 100;
export const MAX_TOP_UP_QUANTITY_LENGTH = 9;
export const UNLIMITED_BANK_ACCOUNTS = -1;
export const USAGE_WARNING_THRESHOLD_PCT = 80;
export const USAGE_FULL_PCT = 100;
export const BILLING_STATUS_PARAM = 'billing';
export const PURCHASE_HISTORY_PREVIEW_COUNT = 5;

export const SIGNATURE_PAD_WIDTH = 270;
export const SIGNATURE_PAD_HEIGHT = 160;
